import { useEffect, useMemo, useState } from 'react'
import { Container, Alert, Card, Table, Badge, Form, Button, Modal, InputGroup, Spinner } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  fetchOrphanedDistributorProducts,
  fetchProducts,
  mergePlaceholderProducts,
  type OrphanedProductDto,
  type ProductDto,
} from '../services/api'

type Suggestion = {
  code: string
  name: string
  matchKey: string
}

export default function OrphanedProducts() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Bulk-consolidate state ─────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [destinationCode, setDestinationCode] = useState('')
  const [destinationName, setDestinationName] = useState('')
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  // ── Per-row "Link to PIM" modal state ─────────────────────────────────────
  const [linkTarget, setLinkTarget] = useState<OrphanedProductDto | null>(null)
  const [pimSearch, setPimSearch] = useState('')
  const [pimResults, setPimResults] = useState<ProductDto[]>([])
  const [pimSearchLoading, setPimSearchLoading] = useState(false)
  const [linkStatus, setLinkStatus] = useState<string | null>(null)

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ['orphaned-distributor-products'],
    queryFn: fetchOrphanedDistributorProducts,
    refetchInterval: 15000,
  })

  const suggestionsById = useMemo(() => {
    const map = new Map<string, Suggestion>()
    for (const row of data ?? []) {
      map.set(row.productId, buildSuggestion(row))
    }
    return map
  }, [data])

  const selectedRows = useMemo(
    () => (data ?? []).filter((r) => selectedIds.includes(r.productId)),
    [data, selectedIds]
  )

  const selectionSuggestion = useMemo(
    () => buildGroupSuggestion(selectedRows, suggestionsById),
    [selectedRows, suggestionsById]
  )

  // ── Mutations ──────────────────────────────────────────────────────────────
  const consolidateMutation = useMutation({
    mutationFn: mergePlaceholderProducts,
    onSuccess: (result) => {
      setStatusMessage(`Consolidated into ${result.destinationCode} (${result.destinationName}).`)
      setSelectedIds([])
      queryClient.invalidateQueries({ queryKey: ['orphaned-distributor-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to consolidate selected products.'
      setStatusMessage(message)
    },
  })

  const linkMutation = useMutation({
    mutationFn: mergePlaceholderProducts,
    onSuccess: (result) => {
      setLinkStatus(`✓ Linked to ${result.destinationCode} — ${result.destinationName}`)
      queryClient.invalidateQueries({ queryKey: ['orphaned-distributor-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      // auto-close after short delay
      setTimeout(() => {
        setLinkTarget(null)
        setLinkStatus(null)
        setPimSearch('')
        setPimResults([])
      }, 1500)
    },
    onError: () => {
      setLinkStatus('Failed to link. Please try again.')
    },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  const allSelected = !!data && data.length > 0 && selectedIds.length === data.length

  const toggleSelection = (id: string) => {
    setStatusMessage(null)
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    setStatusMessage(null)
    if (!data) return
    setSelectedIds(allSelected ? [] : data.map((r) => r.productId))
  }

  const applySuggestedSelection = () => {
    if (!selectionSuggestion) return
    setDestinationCode(selectionSuggestion.code)
    setDestinationName(selectionSuggestion.name)
  }

  const handleConsolidate = () => {
    if (selectedIds.length < 2) {
      setStatusMessage('Select at least 2 orphaned products to consolidate.')
      return
    }
    if (!destinationCode.trim()) {
      setStatusMessage('Enter a destination product code (or use Apply suggestion).')
      return
    }
    consolidateMutation.mutate({
      productIds: selectedIds,
      destinationCode: destinationCode.trim(),
      destinationName: destinationName.trim() || undefined,
    })
  }

  const openLinkModal = (row: OrphanedProductDto) => {
    setLinkTarget(row)
    setLinkStatus(null)
    setPimSearch('')
    setPimResults([])
  }

  const closeLinkModal = () => {
    if (linkMutation.isPending) return
    setLinkTarget(null)
    setLinkStatus(null)
    setPimSearch('')
    setPimResults([])
  }

  const handlePimSearch = async (term: string) => {
    setPimSearch(term)
    if (term.trim().length < 2) { setPimResults([]); return }
    setPimSearchLoading(true)
    try {
      const results = await fetchProducts({ search: term.trim() })
      setPimResults(results.slice(0, 20))
    } catch {
      setPimResults([])
    } finally {
      setPimSearchLoading(false)
    }
  }

  const handleAcceptSuggestion = () => {
    if (!linkTarget) return
    const suggestion = suggestionsById.get(linkTarget.productId)
    if (!suggestion) return
    setLinkStatus('Linking...')
    linkMutation.mutate({
      productIds: [linkTarget.productId],
      destinationCode: suggestion.code,
      destinationName: suggestion.name,
    })
  }

  const handleLinkToExisting = (pimProduct: ProductDto) => {
    if (!linkTarget) return
    setLinkStatus('Linking...')
    linkMutation.mutate({
      productIds: [linkTarget.productId],
      destinationProductId: pimProduct.id,
    })
  }

  useEffect(() => {
    const deepLinkProductId = searchParams.get('productId')
    if (!deepLinkProductId || linkTarget || !data || data.length === 0) return

    const row = data.find((x) => x.productId === deepLinkProductId)
    if (!row) return

    setLinkTarget(row)
    setLinkStatus(null)
    setPimSearch('')
    setPimResults([])
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('productId')
      return next
    }, { replace: true })
  }, [data, linkTarget, searchParams, setSearchParams])

  // ── Render ─────────────────────────────────────────────────────────────────
  const linkTargetSuggestion = linkTarget ? suggestionsById.get(linkTarget.productId) : null

  return (
    <Container className="py-4">
      <Alert variant="warning" className="py-2">
        <strong>Legacy cleanup only.</strong> New distributor discoveries require assigning a Kingspan source-of-truth product
        on <strong>Discover Product Pages</strong> before distributor scrape runs. Use this page to merge older{' '}
        <code>DIST-*</code> placeholder products.
      </Alert>
      <h1>Orphaned products</h1>
      <p className="text-muted">
        Legacy distributor-linked products that do not yet have a Kingspan source-of-truth link.
        Use <strong>Link to PIM</strong> on each row to associate them with a canonical Kingspan product.
      </p>

      {isLoading && <Alert variant="secondary">Loading orphaned products...</Alert>}
      {isError && <Alert variant="danger">Failed to load orphaned products.</Alert>}
      {statusMessage && (
        <Alert variant={statusMessage.toLowerCase().includes('failed') ? 'danger' : 'info'}>
          {statusMessage}
        </Alert>
      )}

      {/* Bulk consolidate */}
      {!!data && data.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title className="mb-2">Bulk consolidate selected placeholders</Card.Title>
            <div className="small text-muted mb-3">
              Select multiple rows that represent the same Kingspan product, then merge them into one canonical record.
            </div>

            <div className="row g-2 align-items-end">
              <Form.Group className="col-md-3">
                <Form.Label className="small">Selected</Form.Label>
                <Form.Control size="sm" value={`${selectedIds.length} row(s)`} readOnly />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label className="small">Suggested code</Form.Label>
                <Form.Control size="sm" value={selectionSuggestion?.code ?? ''} readOnly />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label className="small">Suggested name</Form.Label>
                <Form.Control size="sm" value={selectionSuggestion?.name ?? ''} readOnly />
              </Form.Group>
              <Form.Group className="col-md-2 d-grid">
                <Button size="sm" variant="outline-primary" onClick={applySuggestedSelection} disabled={!selectionSuggestion}>
                  Apply suggestion
                </Button>
              </Form.Group>
            </div>

            <div className="row g-2 align-items-end mt-1">
              <Form.Group className="col-md-4">
                <Form.Label className="small">Canonical product code</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. THERMAROOF-TR26"
                  value={destinationCode}
                  onChange={(e) => setDestinationCode(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="small">Canonical product name</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Used if a new product is created"
                  value={destinationName}
                  onChange={(e) => setDestinationName(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-2 d-grid">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={consolidateMutation.isPending || selectedIds.length < 2}
                  onClick={handleConsolidate}
                >
                  {consolidateMutation.isPending ? 'Consolidating...' : 'Mark as same product'}
                </Button>
              </Form.Group>
            </div>
          </Card.Body>
        </Card>
      )}

      {data && data.length === 0 && (
        <Alert variant="success">No orphaned distributor products found.</Alert>
      )}

      {data && data.length > 0 && (
        <Card>
          <Card.Body>
            <Card.Title className="mb-3">Needs Kingspan linking ({data.length})</Card.Title>
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <Form.Check checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Suggestion</th>
                  <th>Distributors</th>
                  <th>Targets</th>
                  <th>Example URL</th>
                  <th>Last checked</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const suggestion = suggestionsById.get(row.productId)
                  const selected = selectedIds.includes(row.productId)
                  return (
                    <tr key={row.productId} className={selected ? 'table-active' : ''}>
                      <td>
                        <Form.Check checked={selected} onChange={() => toggleSelection(row.productId)} />
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.code}</td>
                      <td>
                        <div>{row.name}</div>
                        <div className="small text-muted">
                          {[row.market, row.family].filter(Boolean).join(' • ') || 'No market/family yet'}
                        </div>
                      </td>
                      <td style={{ minWidth: 180 }}>
                        {suggestion ? (
                          <>
                            <div className="small fw-semibold" style={{ fontFamily: 'monospace' }}>{suggestion.code}</div>
                            <div className="small text-muted">{suggestion.name}</div>
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div>{row.distributorCount}</div>
                        {row.distributorNames.length > 0 && (
                          <div className="small text-muted">{row.distributorNames.join(', ')}</div>
                        )}
                      </td>
                      <td>{row.targetCount}</td>
                      <td style={{ maxWidth: 280 }}>
                        {row.exampleUrls[0] ? (
                          <a href={row.exampleUrls[0]} target="_blank" rel="noreferrer" className="small text-truncate d-block" style={{ maxWidth: 280 }}>
                            {row.exampleUrls[0]}
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-nowrap">{row.lastCheckedUtc ? new Date(row.lastCheckedUtc).toLocaleDateString() : '-'}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Badge bg="warning">Needs link</Badge>
                          {!row.isApproved && <Badge bg="secondary">Draft</Badge>}
                          <Button
                            size="sm"
                            variant="outline-primary"
                            style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}
                            onClick={() => openLinkModal(row)}
                          >
                            Link to PIM
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* ── Per-row Link to PIM modal ───────────────────────────────────── */}
      <Modal show={!!linkTarget} onHide={closeLinkModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Link to PIM product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {linkTarget && (
            <>
              {/* Orphaned product info */}
              <div className="bg-light rounded p-2 mb-3 small">
                <div className="fw-semibold">{linkTarget.name}</div>
                <div className="text-muted font-monospace">{linkTarget.code}</div>
                <div className="text-muted mt-1">
                  {[linkTarget.market, linkTarget.family].filter(Boolean).join(' · ')}
                  {linkTarget.distributorNames.length > 0 && (
                    <> · Found on: {linkTarget.distributorNames.join(', ')}</>
                  )}
                </div>
                {linkTarget.exampleUrls[0] && (
                  <a href={linkTarget.exampleUrls[0]} target="_blank" rel="noreferrer" className="text-truncate d-block mt-1" style={{ maxWidth: '100%' }}>
                    {linkTarget.exampleUrls[0]}
                  </a>
                )}
              </div>

              {/* Option 1: Accept suggestion */}
              {linkTargetSuggestion && (
                <div className="border rounded p-3 mb-3">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div className="fw-semibold small mb-1">Auto-generated suggestion</div>
                      <div className="font-monospace">{linkTargetSuggestion.code}</div>
                      <div className="text-muted small">{linkTargetSuggestion.name}</div>
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                        This will create a new canonical product if one with this code does not exist.
                      </div>
                    </div>
                    <Button
                      variant="outline-success"
                      size="sm"
                      style={{ whiteSpace: 'nowrap' }}
                      disabled={linkMutation.isPending}
                      onClick={handleAcceptSuggestion}
                    >
                      Accept suggestion
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-muted small text-center mb-3">— or search the product library —</div>

              {/* Option 2: Search & choose from PIM */}
              <InputGroup className="mb-2" size="sm">
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Control
                  placeholder="Search products by name or code…"
                  value={pimSearch}
                  onChange={(e) => handlePimSearch(e.target.value)}
                  autoFocus
                />
                {pimSearchLoading && (
                  <InputGroup.Text><Spinner animation="border" size="sm" /></InputGroup.Text>
                )}
              </InputGroup>

              {pimSearch.trim().length >= 2 && pimResults.length === 0 && !pimSearchLoading && (
                <div className="text-muted small text-center py-2">No products found matching "{pimSearch}"</div>
              )}

              {pimResults.length > 0 && (
                <div className="border rounded" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {pimResults.map((p) => (
                    <div
                      key={p.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                      style={{ gap: '1rem' }}
                    >
                      <div className="flex-grow-1 min-width-0">
                        <div className="fw-semibold small">{p.name}</div>
                        <div className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>{p.code}</div>
                        {(p.market || p.family) && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {[p.market, p.family].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {p.kingspanUrl && (
                          <a href={p.kingspanUrl} target="_blank" rel="noreferrer" className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {p.kingspanUrl}
                          </a>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {p.isApproved && <Badge bg="success" style={{ fontSize: '0.65rem' }}>Approved</Badge>}
                        <Button
                          size="sm"
                          variant="primary"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                          disabled={linkMutation.isPending}
                          onClick={() => handleLinkToExisting(p)}
                        >
                          Link to this
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {linkStatus && (
                <Alert
                  variant={linkStatus.startsWith('✓') ? 'success' : linkStatus === 'Linking...' ? 'secondary' : 'danger'}
                  className="mt-3 mb-0 small py-2"
                >
                  {linkStatus === 'Linking...' && <Spinner animation="border" size="sm" className="me-2" />}
                  {linkStatus}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeLinkModal} disabled={linkMutation.isPending}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

// ── Suggestion helpers ──────────────────────────────────────────────────────

function buildGroupSuggestion(rows: OrphanedProductDto[], suggestions: Map<string, Suggestion>): Suggestion | null {
  if (rows.length === 0) return null
  if (rows.length === 1) return suggestions.get(rows[0].productId) ?? null

  const normalizedNames = rows.map((r) => simplifyName(r.name))
  const common = commonMeaningfulTokens(normalizedNames)
  const code = common.length > 0
    ? common.slice(0, 4).map(toCodeToken).join('-')
    : (suggestions.get(rows[0].productId)?.code ?? '')
  const name = common.length > 0
    ? common.slice(0, 6).map(toTitleWord).join(' ')
    : (suggestions.get(rows[0].productId)?.name ?? '')
  const safeCode = code || `CANONICAL-${rows.length}-PRODUCTS`
  const safeName = name || `Consolidated product (${rows.length} distributor listings)`
  return { code: safeCode, name: safeName, matchKey: common.join(' ') }
}

function buildSuggestion(row: OrphanedProductDto): Suggestion {
  const raw = `${decodeURIComponentSafe(row.name)} ${decodeURIComponentSafe(row.exampleUrls[0] ?? '')}`
  const simplified = simplifyName(raw)
  const family = detectFamilyToken(simplified)
  const model = detectModelToken(simplified)
  const qualifiers = detectQualifiers(simplified)

  const codeParts = [family, model, ...qualifiers]
    .filter((v): v is string => Boolean(v))
    .slice(0, 4)
    .map(toCodeToken)
    .filter(Boolean)

  const nameParts = [family ? toTitleWord(family) : '', model?.toUpperCase() ?? '', ...qualifiers.map(toTitleWord)]
    .filter(Boolean)

  const code = codeParts.length > 0 ? codeParts.join('-') : fallbackCodeFromName(row.name)
  const name = nameParts.length > 0 ? nameParts.join(' ') : fallbackName(row.name)
  const matchKey = [family, model, ...qualifiers].filter(Boolean).join(' ')

  return { code, name, matchKey }
}

function simplifyName(value: string) {
  return decodeURIComponentSafe(value)
    .replace(/\(distributor discovery\)/gi, ' ')
    .replace(/%c2%ae/gi, ' ')
    .replace(/[®™]/g, ' ')
    .replace(/\b\d{3,5}x\d{3,5}(x\d{1,4})?(mm|cm|m)?\b/gi, ' ')
    .replace(/\b\d{4,}\b/g, ' ')
    .replace(/[-_/(),.:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function detectFamilyToken(text: string) {
  const families = ['kooltherm', 'thermaroof', 'quadcore', 'powerpanel', 'green guard', 'nilvent', 'ikoustic']
  return families.find((f) => text.includes(f.replace(' ', ''))) || families.find((f) => text.includes(f))
}

function detectModelToken(text: string) {
  const matches = text.match(/\b([a-z]{0,3}\d{1,3}[a-z]?)\b/gi) ?? []
  const preferred = matches.find((m) => /^(k|tr|ks|rw|gg)/i.test(m))
  return (preferred || matches[0] || '').toUpperCase()
}

function detectQualifiers(text: string) {
  const qualifiers = ['external', 'internal', 'wall', 'roof', 'board', 'panel']
  return qualifiers.filter((q) => text.includes(` ${q} `) || text.startsWith(`${q} `) || text.endsWith(` ${q}`))
}

function fallbackCodeFromName(name: string) {
  const tokens = simplifyName(name)
    .split(' ')
    .filter((t) => t && !['kingspan', 'distributor', 'discovery'].includes(t))
    .slice(0, 4)
    .map(toCodeToken)
  return tokens.join('-') || 'UNMATCHED-PRODUCT'
}

function fallbackName(name: string) {
  return decodeURIComponentSafe(name).replace(/\(Distributor discovery\)/i, '').trim()
}

function toCodeToken(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function toTitleWord(value: string) {
  return value
    .split(/[\s-]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ')
}

function commonMeaningfulTokens(values: string[]) {
  if (values.length === 0) return []
  const tokenLists = values.map((v) =>
    v.split(' ').filter((t) => t.length > 1 && !['kingspan', 'product', 'distributor', 'discovery', 'board'].includes(t))
  )
  const first = tokenLists[0]
  return first.filter((token) => tokenLists.every((tokens) => tokens.includes(token)))
}

function decodeURIComponentSafe(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
